import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Save, Scale } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useBuildBuyProtocol } from "@/hooks/useBuildBuyEvaluation";

interface Props {
  orgId: string | undefined;
}

export function BuildBuyProtocolTab({ orgId }: Props) {
  const { data, isLoading, save, placeholder } = useBuildBuyProtocol(orgId);
  const [text, setText] = useState("");

  useEffect(() => {
    if (typeof data === "string") setText(data);
  }, [data]);

  const handleSave = async () => {
    try {
      await save.mutateAsync(text);
      toast({ title: "Protocolo guardado", description: "Se aplicará en las próximas evaluaciones Build/Buy/Outsource." });
    } catch {
      toast({ title: "Error", description: "No se pudo guardar el protocolo.", variant: "destructive" });
    }
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary" />
          Protocolo Build / Buy / Outsource
        </CardTitle>
        <CardDescription>
          Escribe en lenguaje natural las reglas de tu empresa para decidir entre comprar un SaaS, desarrollar internamente o tercerizar.
          La IA usará este texto junto con el diagnóstico, el CoE, los riesgos y la normativa al evaluar cada proyecto.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            className="min-h-[280px] font-mono text-sm leading-relaxed"
          />
        )}
        <div className="flex justify-between items-center pt-2">
          <p className="text-xs text-muted-foreground">
            {text.length} caracteres · Se guarda un snapshot en cada evaluación para auditoría.
          </p>
          <Button onClick={handleSave} disabled={save.isPending || isLoading} className="gap-2">
            {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar Protocolo
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
